import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Accordion,
    Alert,
    AccordionDetails,
    AccordionSummary,
    Badge,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Popover,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import SortOutlinedIcon from "@mui/icons-material/SortOutlined";

import SysColumn from "app/class/sys-column";
import { LogicalNode } from "app/types/record-management-types";
import { FilterBuilder } from "modules/record/record-producer/components/record-producer-fields/condition-field/FilterBuilder";
import { LogicalNode as BuilderLogicalNode } from "modules/record/record-producer/types/query.types";
import {
    createBuilderFilterFromApplied,
    sanitizeBuilderFilterWithReport,
} from "../services/recordListUrlState";
import Translate from "component/global/translation/translation-component";
import { useTranslation } from "react-i18next";
import { normalizeSortPreferences, SortPreference, SortPreferences } from "../hooks/useRecordListSort";
import { translateMetadataLabel } from "modules/record/shared/services/recordMetadataLabels";

interface RecordListFilterControlProps {
    appliedFilter: LogicalNode | null;
    columns: SysColumn[];
    onApply: (filter: LogicalNode | null) => void;
    onClear: () => void;
    sortPreferences: SortPreferences | null;
    onApplySort: (sortPreferences: SortPreferences | null) => void;
}

const countConditions = (node: LogicalNode | null): number => {
    if (!node) {
        return 0;
    }

    return node.conditions.reduce((count, child) => {
        if (child.type === "condition") {
            return count + 1;
        }

        if (child.type === "exists") {
            return count + child.conditions.length;
        }

        return count + countConditions(child);
    }, 0);
};

const secondaryActionButtonSx = {
    color: "text.primary",
    borderColor: "divider",
    bgcolor: "transparent",
    "&:hover": {
        borderColor: "primary.main",
        bgcolor: "action.hover",
    },
    "&.Mui-disabled": {
        color: "text.disabled",
        borderColor: "divider",
        bgcolor: "transparent",
    },
};

const RecordListFilterControl = ({
    appliedFilter,
    columns,
    onApply,
    onClear,
    sortPreferences,
    onApplySort,
}: RecordListFilterControlProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [draftFilter, setDraftFilter] = useState<BuilderLogicalNode>(
        () => createBuilderFilterFromApplied(appliedFilter)
    );
    const draftFilterRef = useRef(draftFilter);
    const [draftSort, setDraftSort] = useState<SortPreference[]>(
        () => normalizeSortPreferences(sortPreferences) ?? []
    );
    const draftSortRef = useRef(draftSort);
    const [sortExpanded, setSortExpanded] = useState(false);
    const [discardWarning, setDiscardWarning] = useState<ReturnType<typeof sanitizeBuilderFilterWithReport>["partialApplication"]>(null);

    const open = Boolean(anchorEl);
    const appliedCount = useMemo(() => countConditions(appliedFilter), [appliedFilter]);
    const appliedSortCount = normalizeSortPreferences(sortPreferences)?.length ?? 0;
    const availableColumns = useMemo(
        () => columns
            .filter((column) => column.active !== false && Boolean(column.name))
            .map((column) => ({
                name: column.name,
                label: translateMetadataLabel(t, column),
            })),
        [columns, t]
    );

    const updateDraftFilter = useCallback((nextDraftFilter: BuilderLogicalNode) => {
        draftFilterRef.current = nextDraftFilter;
        setDraftFilter(nextDraftFilter);
        const report = sanitizeBuilderFilterWithReport(nextDraftFilter, columns).partialApplication;
        const meaningfulDiscard = report && report.discarded.some((item) => item.reason === "invalid");
        setDiscardWarning(meaningfulDiscard ? report : null);
    }, [columns]);

    const updateDraftSort = useCallback((nextDraftSort: SortPreference[] | ((current: SortPreference[]) => SortPreference[])) => {
        const resolvedDraftSort = typeof nextDraftSort === "function"
            ? nextDraftSort(draftSortRef.current)
            : nextDraftSort;

        draftSortRef.current = resolvedDraftSort;
        setDraftSort(resolvedDraftSort);
    }, []);

    useEffect(() => {
        if (!open) {
            updateDraftFilter(createBuilderFilterFromApplied(appliedFilter));
            updateDraftSort(normalizeSortPreferences(sortPreferences) ?? []);
            setSortExpanded(appliedSortCount > 0);
        }
    }, [appliedFilter, appliedSortCount, open, sortPreferences, updateDraftFilter, updateDraftSort]);

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        updateDraftFilter(createBuilderFilterFromApplied(appliedFilter));
        updateDraftSort(normalizeSortPreferences(sortPreferences) ?? []);
        setSortExpanded(appliedSortCount > 0);
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setDiscardWarning(null);
    };
    const handleApply = () => {
        const result = sanitizeBuilderFilterWithReport(draftFilterRef.current, columns);
        const meaningfulDiscard = result.partialApplication && result.partialApplication.discarded.length > 0;
        setDiscardWarning(meaningfulDiscard ? result.partialApplication : null);

        if (meaningfulDiscard) {
            if (result.filter) {
                onApply(result.filter);
                onApplySort(normalizeSortPreferences(draftSortRef.current));
            }
            return;
        }

        onApply(result.filter);
        onApplySort(normalizeSortPreferences(draftSortRef.current));
        handleClose();
    };

    const handleClear = () => {
        updateDraftFilter(createBuilderFilterFromApplied(null));
        updateDraftSort([]);
        setDiscardWarning(null);
        onClear();
        onApplySort(null);
        handleClose();
    };

    const handleAddSort = () => {
        const selectedFields = new Set(draftSortRef.current.map(({ field }) => field).filter(Boolean));
        const nextField = availableColumns.find((column) => !selectedFields.has(column.name))?.name ?? "";
        updateDraftSort((current) => [...current, { field: nextField, sort: "asc" }]);
        setSortExpanded(true);
    };

    const handleUpdateSort = (index: number, patch: Partial<SortPreference>) => {
        updateDraftSort((current) => current.map((sort, currentIndex) => (
            currentIndex === index ? { ...sort, ...patch } : sort
        )));
    };

    const handleRemoveSort = (index: number) => {
        updateDraftSort((current) => current.filter((_, currentIndex) => currentIndex !== index));
    };

    const discardWarningAlert = discardWarning ? (
        <Alert severity="warning" role="alert" data-partial-reason={discardWarning.reason}>
            {t("record.list.filter.conditions_discarded", {
                fields: discardWarning.discarded.map(({ field }) => field).join(", "),
            })}
        </Alert>
    ) : null;

    const sortEditor = (
        <Accordion
            expanded={sortExpanded}
            onChange={(_, expanded) => setSortExpanded(expanded)}
            disableGutters
            elevation={0}
            sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: (theme) => `${Math.min(Number(theme.shape.borderRadius) || 0, 10)}px !important`,
                overflow: "hidden",
                "&:before": { display: "none" },
            }}
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                    <SortOutlinedIcon fontSize="small" color={draftSort.length > 0 ? "primary" : "inherit"} />
                    <Typography variant="subtitle2" fontWeight={600}>
                        {t("Sorting")}
                    </Typography>
                    {draftSort.length > 0 && (
                        <Typography variant="caption" color="text.secondary">
                            {draftSort.length}
                        </Typography>
                    )}
                </Stack>
            </AccordionSummary>
            <AccordionDetails>
                <Stack spacing={1.5}>
                    {draftSort.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            {t("No sorting configured")}
                        </Typography>
                    ) : (
                        draftSort.map((sort, index) => (
                            <Stack
                                key={`${sort.field || "sort"}-${index}`}
                                direction={{ xs: "column", sm: "row" }}
                                spacing={1}
                                alignItems={{ xs: "stretch", sm: "center" }}
                            >
                                <TextField
                                    select
                                    size="small"
                                    label={index === 0 ? t("Sort by") : t("Then by")}
                                    value={sort.field}
                                    onChange={(event) => handleUpdateSort(index, { field: event.target.value })}
                                    sx={{ flex: 1, minWidth: { sm: 260 } }}
                                >
                                    {availableColumns.map((column) => {
                                        const usedByAnotherSort = draftSort.some(
                                            (draft, currentIndex) => currentIndex !== index && draft.field === column.name
                                        );

                                        return (
                                            <MenuItem key={column.name} value={column.name} disabled={usedByAnotherSort}>
                                                {column.label}
                                            </MenuItem>
                                        );
                                    })}
                                </TextField>
                                <TextField
                                    select
                                    size="small"
                                    label={t("Direction")}
                                    value={sort.sort}
                                    onChange={(event) => handleUpdateSort(index, {
                                        sort: event.target.value === "desc" ? "desc" : "asc",
                                    })}
                                    sx={{ minWidth: { sm: 180 } }}
                                >
                                    <MenuItem value="asc">{t("Ascending")}</MenuItem>
                                    <MenuItem value="desc">{t("Descending")}</MenuItem>
                                </TextField>
                                <IconButton
                                    size="small"
                                    color="inherit"
                                    aria-label={String(t("Remove sort"))}
                                    onClick={() => handleRemoveSort(index)}
                                    sx={{ alignSelf: { xs: "flex-end", sm: "center" } }}
                                >
                                    <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                            </Stack>
                        ))
                    )}
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between">
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AddIcon fontSize="small" />}
                            onClick={handleAddSort}
                            disabled={availableColumns.length === 0 || draftSort.length >= availableColumns.length}
                        >
                            <Translate text="Add sort" />
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            color="inherit"
                            onClick={() => updateDraftSort([])}
                            disabled={draftSort.length === 0}
                            sx={secondaryActionButtonSx}
                        >
                            <Translate text="Clear sorting" />
                        </Button>
                    </Stack>
                </Stack>
            </AccordionDetails>
        </Accordion>
    );

    return (
        <>
            <Badge
                color="warning"
                badgeContent={appliedCount > 0 ? appliedCount : 0}
                invisible={appliedCount === 0}
                sx={{ width: { xs: "100%", sm: "auto" } }}
            >
                <Button
                    variant="contained"
                    color="inherit"
                    size="small"
                    // disabled={appliedCount > 0 ? false : true}
                    onClick={handleOpen}
                    startIcon={<FilterAltOutlinedIcon fontSize="small" />}
                    sx={{
                        minWidth: 0,
                        width: { xs: "100%", sm: "auto" },
                        height: { xs: 40, sm: 32 },
                        justifyContent: { xs: "flex-start", sm: "center" },
                        backgroundColor: (theme) =>
                            theme.palette.mode === "dark" ? "action.selected" : theme.palette.grey[200],
                        color: "text.primary",
                        "&:hover": {
                            backgroundColor: (theme) =>
                                theme.palette.mode === "dark" ? "action.hover" : theme.palette.grey[300],
                        },
                    }}
                >
                    <Translate fontSize="small" text="Filter" />
                </Button>
            </Badge>
            {isMobile ? (
                <Dialog open={open} onClose={handleClose} fullScreen>
                    <DialogTitle>{t("Record Filters")}</DialogTitle>
                    <DialogContent sx={{ px: 2 }}>
                        <Stack spacing={2}>
                            <Typography variant="body2" color="text.secondary">
                                {t("Changes only affect the list after you apply them.")}
                            </Typography>
                            {discardWarningAlert}

                            <FilterBuilder
                                value={draftFilter}
                                columns={columns}
                                guided
                                onChange={(value) => updateDraftFilter(value as BuilderLogicalNode)}
                            />
                            {sortEditor}
                        </Stack>
                    </DialogContent>
                    <DialogActions
                        sx={{
                            px: 2,
                            pb: "calc(16px + env(safe-area-inset-bottom))",
                        }}
                    >
                        <Stack direction="column-reverse" spacing={1} sx={{ width: "100%" }}>
                            <Button
                                size="small"
                                variant="outlined"
                                color="inherit"
                                onClick={handleClose}
                                fullWidth
                                sx={secondaryActionButtonSx}
                            >
                                <Translate text="Close" />
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                color="inherit"
                                onClick={handleClear}
                                fullWidth
                                sx={secondaryActionButtonSx}
                            >
                                <Translate text="Clear" />
                            </Button>
                            <Button size="small" variant="contained" onClick={handleApply} fullWidth>
                                <Translate text="Apply" />
                            </Button>
                        </Stack>
                    </DialogActions>
                </Dialog>
            ) : (
                <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    PaperProps={{
                        sx: {
                            width: {
                                xs: "calc(100vw - 32px)",
                                md: "min(920px, calc(100vw - 32px))",
                                lg: "min(1080px, calc(100vw - 32px))",
                            },
                            maxWidth: "calc(100vw - 32px)",
                            maxHeight: "calc(100vh - 96px)",
                            overflow: "auto",
                            p: 2,
                        },
                    }}
                >
                    <Stack spacing={2}>
                        <Box>
                            <Typography variant="subtitle1" fontWeight={600}>
                                {t("Record Filters")}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t("Changes only affect the list after you apply them.")}
                            </Typography>

                            {discardWarningAlert}
                        </Box>
                        <FilterBuilder
                            value={draftFilter}
                            columns={columns}
                            guided
                            onChange={(value) => updateDraftFilter(value as BuilderLogicalNode)}
                        />
                        {sortEditor}
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button
                                size="small"
                                variant="outlined"
                                color="inherit"
                                onClick={handleClose}
                                sx={secondaryActionButtonSx}
                            >
                                <Translate text="Close" />
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                color="inherit"
                                onClick={handleClear}
                                sx={secondaryActionButtonSx}
                            >
                                <Translate text="Clear" />
                            </Button>
                            <Button size="small" variant="contained" onClick={handleApply}>
                                <Translate text="Apply" />
                            </Button>
                        </Stack>
                    </Stack>
                </Popover>
            )}
        </>
    );
};

export default RecordListFilterControl;
